import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  TextField,
  TablePagination,
  TableSortLabel,
  InputAdornment,
  Button,
  Avatar,
  useTheme,
  alpha,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  Snackbar,
  Select
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import baseUrl from '../Config';


const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: alpha('#4BB648', 0.05),
  },
  '&:hover': {
    backgroundColor: alpha('#4BB648', 0.15),
    transition: 'background-color 0.2s ease',
  },
}));

const AdminList = () => {
  const theme = useTheme();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    designation: '',
    role: ''
  });

  const token = sessionStorage.getItem("token");

  console.log("Admins data:", admins); // Debugging line to check admins data

  const fetchAdmins = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${baseUrl}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Fetched admins response:", response);
      // Map backend field names to frontend expected names
      const adminsArr = response.data.map(admin => ({
        id: admin.id,
        fullname: admin.name, 
        name: admin.name,
        email: admin.email,
        phoneNumber: admin.phoneNumber,
        designation: admin.designation,
        role: admin.role,
        createdAt: admin.createdAt
      }));
      
      setAdmins(adminsArr);
    } catch (err) {
      setError('Failed to fetch admin users. Please try again.');
      setAdmins([]);
      console.error('Error fetching admins:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleEditClick = (admin) => {
    console.log('Editing admin:', admin); 
    setCurrentAdmin(admin);
    setEditForm({
      name: admin.name,
      email: admin.email,
      phoneNumber: admin.phoneNumber,
      designation: admin.designation,
      role: admin.role
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentAdmin(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      await axios.put(
        `${baseUrl}/users/${currentAdmin.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('Admin updated successfully');
      fetchAdmins();
      handleCloseDialog();
    } catch (err) {
      console.error('Error updating admin:', err);
      setError(err.response?.data?.message || 'Failed to update admin');
    }
  };

  const handleDelete = async (adminId) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      try {
        await axios.delete(
          `${baseUrl}/users/${adminId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setSuccess('Admin deleted successfully');
        fetchAdmins();
      } catch (err) {
        console.error('Error deleting admin:', err);
        setError('Failed to delete admin. You may not have permission.');
      }
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  const filteredAdmins = admins.filter((admin) =>
    Object.values(admin).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const sortedAdmins = filteredAdmins.sort((a, b) => {
    if (a[orderBy] < b[orderBy]) {
      return order === 'asc' ? -1 : 1;
    }
    if (a[orderBy] > b[orderBy]) {
      return order === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const paginatedAdmins = sortedAdmins.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getInitials = (name) => {
    return name ? name.split(' ').map(part => part[0]).join('').toUpperCase() : '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading && admins.length === 0) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="300px"
        flexDirection="column"
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" mt={3} color="textSecondary">
          Loading admin data...
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ 
      borderRadius: 2,
      overflow: 'hidden',
      border: `1px solid ${theme.palette.divider}`,
      marginTop: '70px',
    }}>

       <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored" // or "light"
      />
    
      {/* <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar> */}
      
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      
      <Box sx={{
        p: 3,
        display: 'flex',
        justifyContent: 'space-between',
        // alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}>
        <Box mt={2}>
        <Typography variant="h5" component="h1" fontWeight="fontWeightBold">
          Admin List
        </Typography>
        <Typography variant="body2" color="textSecondary" mt={1}>
          {filteredAdmins.length} admin(s) found
        </Typography>
</Box>

        <Box mt={2}>
           <TextField
          variant="outlined"
          placeholder="Search admins by name, email, phone..."
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 1,
            }
          }}
          sx={{ mb: 3, maxWidth: 500 , width: '400px' }}
        />
        </Box>
      </Box>
      
      <Box sx={{ p: 3 }}>
        {/* <TextField
          variant="outlined"
          placeholder="Search admins by name, email, phone..."
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 1,
            }
          }}
          sx={{ mb: 3, maxWidth: 500 }}
        /> */}
        
        <TableContainer sx={{ 
          maxHeight: 'calc(100vh - 300px)',
          overflow: 'auto',
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
        }}>
          <Table stickyHeader aria-label="admin table" size="medium">
            <TableHead>
              <TableRow>
                <TableCell width="60px"></TableCell>
                {[
                  { id: 'name', label: 'Name' },
                  { id: 'email', label: 'Email' },
                  { id: 'phoneNumber', label: 'Phone' },
                  { id: 'designation', label: 'Designation' },
                  { id: 'role', label: 'Role' },
                  { id: 'createdAt', label: 'Joined' },
                ].map((headCell) => (
                  <TableCell 
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      fontWeight: 'fontWeightBold',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell 
                  width="80px" 
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    fontWeight: 'fontWeightBold',
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAdmins.length > 0 ? (
                paginatedAdmins.map((admin) => (
                  <StyledTableRow key={admin.id} hover>
                    <TableCell>
                      <Avatar sx={{ 
                        bgcolor: '#4BB648',
                        width: 36, 
                        height: 36,
                        fontSize: 14,
                      }}>
                        {getInitials(admin.name)}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="fontWeightMedium">
                        {admin.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>{admin.phoneNumber || 'N/A'}</TableCell>
                    <TableCell>
                      <Box 
                        component="span" 
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: alpha('#4BB648', 0.1),
                          color: '#4BB648',
                          fontSize: 12,
                          fontWeight: 'medium',
                        }}
                      >
                        {admin.designation || 'N/A'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box 
                        component="span" 
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: admin.role === 'superadmin' 
                            ? alpha(theme.palette.error.main, 0.1)
                            : admin.role === 'admin' 
                            ? alpha(theme.palette.success.main, 0.1) 
                            : alpha(theme.palette.warning.main, 0.1),
                          color: admin.role === 'superadmin' 
                            ? theme.palette.error.dark
                            : admin.role === 'admin' 
                            ? theme.palette.success.dark 
                            : theme.palette.warning.dark,
                          fontSize: 12,
                          fontWeight: 'medium',
                        }}
                      >
                        {admin.role}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {formatDate(admin.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell width="80px">
                      <Box display="flex" gap={1}>
                        <IconButton
                          onClick={() => handleEditClick(admin)}
                          size="small"
                          sx={{
                            color: '#4BB648',
                            '&:hover': {
                              backgroundColor: alpha('#4BB648', 0.1),
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(admin.id)}
                          size="small"
                          sx={{
                            color: theme.palette.error.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.error.main, 0.1),
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </StyledTableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <SearchIcon fontSize="large" color="disabled" />
                      <Typography variant="h6" color="textSecondary" mt={1}>
                        No admins found
                      </Typography>
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        Try adjusting your search query
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Admin</DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ mt: 2 }}>
              <TextField
                margin="normal"
                fullWidth
                label="Full Name"
                name="name"
                value={editForm.name}
                onChange={handleFormChange}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Email"
                name="email"
                value={editForm.email}
                onChange={handleFormChange}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={editForm.phoneNumber}
                onChange={handleFormChange}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Designation"
                name="designation"
                value={editForm.designation}
                onChange={handleFormChange}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  name="role"
                  value={editForm.role}
                  onChange={handleFormChange}
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="staff">Staff</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              sx={{
                color: '#4BB648',
                backgroundColor: 'transparent',
                border: `1px solid #4BB648`,
              }}
              onClick={handleCloseDialog}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              variant="contained"
              sx={{
                backgroundColor: '#4BB648',
                '&:hover': {
                  backgroundColor: '#3D8B3A',
                }
              }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredAdmins.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            pt: 2,
          }}
        />
      </Box>
    </Paper>
  );
};

export default AdminList;


